// =============================================================================
// app/src/paginas/FiadoConta.tsx — UMA conta de fiado (uma venda) e o acerto.
//
// Saiu de Fiado.tsx em 26/09/2026, quando nasceu a visão "por pessoa": as
// duas visões mostram a mesma conta com as mesmas ações (receber, devolver
// peça, dar desconto, dar como perdida), e o balcão não pode aprender duas.
// Um cartão só, usado nas duas.
// =============================================================================
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FiadoRow, PaymentMethod } from '@core/types';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import CampoNumero from '../componentes/CampoNumero';
import PainelDevolucao from '../componentes/PainelDevolucao';
import {
  darDescontoFiado, devolucoesDaVenda, itensDaVendaParaGerencia, perderFiado,
  quitacaoDaVenda, receberFiado,
} from '../lib/consultas';
import { nomeDoItemVendido } from '../lib/coringa';
import { estaAtrasada } from '../lib/fiado';
import { brl, dataBR, hojeISO } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import { podeVerFinancas, podeVerVendasDeTodos, useSessao } from '../lib/sessao';

/** O que muda quando uma conta muda — nas duas visões. */
export function useRecarregarFiado(): () => void {
  const cliente = useQueryClient();
  return () => {
    cliente.invalidateQueries({ queryKey: ['fiados'] });
    // Os acertos com data: é deles que sai o prazo médio da visão por pessoa.
    cliente.invalidateQueries({ queryKey: ['fiado-acertos'] });
    // O acerto lança entrada no caixa — a tela do caixa muda junto.
    cliente.invalidateQueries({ queryKey: ['caixa'] });
    // O recibo da aba Vendas lê a quitação (saldo, desconto, peças pagas) do
    // mesmo cache: sem isto ele continuaria imprimindo "A RECEBER" numa venda
    // que acabou de ser quitada aqui.
    cliente.invalidateQueries({ queryKey: ['quitacao'] });
  };
}

type Painel = 'receber' | 'devolver' | 'desconto' | 'perder';

/**
 * A conta, com as ações do acerto. Um painel aberto por vez: abrir "Receber"
 * fecha o "dar desconto" da mesma conta, e não se acerta duas coisas ao mesmo
 * tempo na mesma dívida.
 *
 * `dentroDaPessoa`: na visão por pessoa o nome já está no alto da linha, e
 * repetir em cada venda seria ruído — o título vira a DATA da venda.
 */
export function CartaoDoFiado({ f, hoje, aoMudar, dentroDaPessoa = false }: {
  f: FiadoRow;
  hoje: string;
  aoMudar: () => void;
  dentroDaPessoa?: boolean;
}) {
  const { papel } = useSessao();
  const [painel, setPainel] = useState<Painel | null>(null);
  const [motivo, setMotivo] = useState('');

  const perder = useMutation({
    mutationFn: () => perderFiado(f.id, motivo),
    onSuccess: () => { setPainel(null); setMotivo(''); aoMudar(); },
  });

  const descontar = useMutation({
    mutationFn: (p: { valor: number; motivo: string }) =>
      darDescontoFiado({ contaId: f.id, valor: p.valor, motivo: p.motivo }),
    onSuccess: () => { setPainel(null); aoMudar(); },
  });

  const quitado = Number(f.saldo) <= 0;
  const velho = !quitado && !f.perdido_em && estaAtrasada(f, hoje);
  const situacao = (
    <>
      {f.perdido_em && <span className="ml-2 text-xs text-dim">(dada como perdida)</span>}
      {quitado && !f.perdido_em && <span className="ml-2 text-xs text-ok">(quitada)</span>}
    </>
  );

  // A cor da borda numa classe SÓ: `border-edge` e `border-alerta/50` juntas
  // disputariam a mesma propriedade, e quem ganha é a ordem do CSS gerado.
  const moldura = dentroDaPessoa
    ? `border ${velho ? 'border-alerta/50' : 'border-edge'}`
    : `cartao ${velho ? 'border-alerta/50' : ''}`;

  return (
    <div className={`p-3 ${moldura}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold">
            {dentroDaPessoa ? `Venda de ${dataBR(f.vendida_em)}` : f.cliente}
            {situacao}
          </p>
          <p className="text-xs text-mute">
            {!dentroDaPessoa && <>Venda de {dataBR(f.vendida_em)} · </>}
            {f.vendedor}
            {' · '}
            <span className={velho ? 'font-bold text-alerta' : ''}>
              {f.dias_em_aberto} {f.dias_em_aberto === 1 ? 'dia' : 'dias'}
            </span>
            {Number(f.recebido) > 0 && ` · já pagou ${brl(Number(f.recebido))}`}
            {f.recebimentos > 1 ? ` em ${f.recebimentos} vezes` : ''}
            {/* Cada abatimento em linha própria, nunca somado ao "já
                pagou": são histórias diferentes — uma é dinheiro que
                entrou, outra é dinheiro de que a loja abriu mão, e a
                terceira é peça que voltou para a prateleira. */}
            {Number(f.desconto) > 0 && ` · desconto ${brl(Number(f.desconto))}`}
            {Number(f.devolvido) > 0 && ` · devolveu ${brl(Number(f.devolvido))}`}
          </p>
          {f.motivo_perda && <p className="text-xs text-dim">{f.motivo_perda}</p>}
          {/* A marca das vendas anteriores ao controle: o dinheiro
              delas já foi (indevidamente) contado no caixa, e o
              acerto não lança de novo. Dizer isso na tela evita a
              pergunta "por que o caixa não mudou?". */}
          {f.caixa_ja_lancado && (
            <p className="mt-1 text-xs text-alerta">
              Venda anterior ao controle de fiado: o valor já foi contado no
              caixa no dia da venda, então o acerto aqui não lança de novo.
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className={`text-lg font-black tabular-nums ${quitado ? 'text-dim line-through' : ''}`}>
            {brl(Number(f.saldo))}
          </p>
          <p className="text-xs text-dim">de {brl(Number(f.valor))}</p>
        </div>
      </div>

      {/* O link para a venda fica FORA de qualquer botão — <a> dentro
          de <button> é HTML inválido (a lição das outras telas). */}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        {/* Leva à venda CERTA: o dia dela no filtro e a sanfona já
            aberta. Antes era só "/vendas", e a aba abre nos últimos
            7 dias — fiado mais velho que isso caía numa lista onde
            a venda não estava.
            `hojeISO(new Date(...))` e NUNCA `vendida_em.slice(0,10)`:
            a segunda forma pega o dia em UTC, e venda das 22h cairia
            no dia seguinte — a mesma armadilha de fuso que já mordeu
            o dataBR desta tela. */}
        {/* Só para quem enxerga a venda do outro, senão o atalho cai numa
            lista onde ela não está — pior do que não ter atalho. Em 28/09/2026
            isso era só o dono; desde 07/10 o gerente voltou a ver a lista de
            todos (sem nenhuma soma), e com ela o atalho, que é justamente o
            gesto de quem está cobrando o fiado de uma venda que não é dele. */}
        {podeVerVendasDeTodos(papel) && (
          <Link className="text-xs text-dim underline"
                to={`/vendas?venda=${f.sale_id}&dia=${hojeISO(new Date(f.vendida_em))}`}>
            ver nas vendas
          </Link>
        )}
        {!quitado && !f.perdido_em && (
          <>
            <button type="button" className="botao-secundario px-3 py-1 text-xs"
                    onClick={() => setPainel('receber')}>
              Receber
            </button>
            {/* Sem gate próprio: a rota /fiado já é de gerente e dono
                (App.tsx), e desconto é exatamente desses dois. Quem
                nega de verdade é o exige_papel dentro da RPC. */}
            {/* Devolver DAQUI (28/08): antes só existia pela aba
                Vendas, que abre filtrada nos últimos 7 dias — e no
                dia do relato 9 dos 36 fiados em aberto eram mais
                velhos que isso, então a venda nem aparecia. Com o
                cliente no balcão acertando a dívida, este é o lugar. */}
            <button type="button" className="text-xs text-dim underline"
                    onClick={() => setPainel('devolver')}>
              devolver peça
            </button>
            <button type="button" className="text-xs text-dim underline"
                    onClick={() => { setPainel('desconto'); descontar.reset(); }}>
              dar desconto
            </button>
            {podeVerFinancas(papel) && (
              <button type="button" className="text-xs text-erro underline"
                      onClick={() => { setPainel('perder'); setMotivo(''); perder.reset(); }}>
                dar como perdida
              </button>
            )}
          </>
        )}
      </div>

      {painel === 'receber' && (
        <PainelAcerto fiado={f} aoFechar={() => setPainel(null)}
                      aoReceber={() => { setPainel(null); aoMudar(); }} />
      )}

      {painel === 'devolver' && (
        <DevolucaoDoFiado fiado={f} aoFechar={() => setPainel(null)}
                          aoRegistrar={() => { setPainel(null); aoMudar(); }} />
      )}

      {painel === 'desconto' && (
        <PainelDesconto
          fiado={f}
          erro={descontar.error ? mensagemDeErro(descontar.error) : null}
          salvando={descontar.isPending}
          aoFechar={() => { setPainel(null); descontar.reset(); }}
          aoConfirmar={(valor, motivoTexto) =>
            descontar.mutate({ valor, motivo: motivoTexto })}
        />
      )}

      {painel === 'perder' && (
        <div className="mt-2 space-y-2 border border-erro/40 bg-erro/5 p-3">
          <p className="text-xs text-mute">
            Fecha a dívida <strong>sem dinheiro</strong> — é reconhecer o prejuízo.
            Não mexe no caixa: nada entrou, e é esse o ponto.
          </p>
          <input className="campo py-2 text-sm" value={motivo} autoFocus
                 placeholder="Motivo (obrigatório): cliente sumiu, acordo…"
                 onChange={(e) => setMotivo(e.target.value)} />
          {perder.error ? <Aviso tipo="erro">{mensagemDeErro(perder.error)}</Aviso> : null}
          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                    onClick={() => setPainel(null)}>Voltar</button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                    disabled={perder.isPending || motivo.trim().length < 3}
                    onClick={() => perder.mutate()}>
              Dar como perdida
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Devolver peça DAQUI, com o cliente na frente acertando a dívida (28/08).
 *
 * É o caso que o dono descreveu: o parceiro B2B vem pagar o fiado, paga 2 das
 * 5 peças e devolve as outras 3 — a dívida tem que virar só as 2 que ficaram.
 * O painel é o mesmo da aba Vendas; o que muda é o caminho até ele.
 *
 * Este componente existe só para BUSCAR o que o painel precisa (as peças da
 * venda e o quanto de cada uma já voltou) — a tela do fiado não carrega isso
 * até alguém pedir.
 */
function DevolucaoDoFiado({ fiado, aoFechar, aoRegistrar }: {
  fiado: FiadoRow; aoFechar: () => void; aoRegistrar: () => void;
}) {
  // PELA FUNÇÃO DE GERÊNCIA, e não por `sale_items` (28/09/2026). A tela toda
  // do fiado é de gerente e dono (App.tsx), e a venda fiada quase sempre é de
  // OUTRA pessoa — quem cobra não é quem vendeu. Com a leitura direta da
  // tabela, a policy nova devolveria zero peça ao gerente, sem erro nenhum: o
  // painel abriria vazio e a devolução do fiado, que foi pedida pelo dono em
  // 28/08, voltaria a ser impossível — agora em silêncio.
  const itens = useQuery({
    queryKey: ['itens-venda', fiado.sale_id],
    queryFn: () => itensDaVendaParaGerencia(fiado.sale_id),
  });
  const devolucoes = useQuery({
    queryKey: ['devolucoes-venda', fiado.sale_id],
    queryFn: () => devolucoesDaVenda(fiado.sale_id),
  });

  const jaDevolvido: Record<string, number> = {};
  for (const d of devolucoes.data ?? []) {
    for (const i of d.itens) {
      jaDevolvido[i.sale_item_id] = (jaDevolvido[i.sale_item_id] ?? 0) + Number(i.quantidade);
    }
  }

  if (itens.isPending || devolucoes.isPending) {
    return <div className="mt-2"><Carregando texto="Carregando as peças da venda" /></div>;
  }
  if (itens.error || devolucoes.error) {
    return (
      <div className="mt-2">
        <Aviso tipo="erro">{mensagemDeErro(itens.error ?? devolucoes.error)}</Aviso>
      </div>
    );
  }

  return (
    <PainelDevolucao
      saleId={fiado.sale_id}
      itens={itens.data ?? []}
      jaDevolvido={jaDevolvido}
      contexto="fiado"
      aoFechar={aoFechar}
      aoRegistrar={aoRegistrar}
    />
  );
}

/** O desconto do acerto com oficina (065): abate a dívida, sem tocar no caixa. */
function PainelDesconto({ fiado, erro, salvando, aoFechar, aoConfirmar }: {
  fiado: FiadoRow;
  erro: string | null;
  salvando: boolean;
  aoFechar: () => void;
  aoConfirmar: (valor: number, motivo: string) => void;
}) {
  const [valor, setValor] = useState<number | null>(null);
  const [motivo, setMotivo] = useState('');
  const saldo = Number(fiado.saldo);
  const demais = (valor ?? 0) > saldo;

  return (
    <div className="mt-2 space-y-2 border border-signal/40 bg-signal/5 p-3">
      <p className="text-xs text-mute">
        Abate a dívida de <strong>{brl(saldo)}</strong> — é o acerto com a oficina.
        Não mexe na venda nem no caixa: não entrou dinheiro, só se cobra menos.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor={`fia-desc-${fiado.id}`}>Desconto (R$)</label>
          <CampoNumero id={`fia-desc-${fiado.id}`} className="campo py-2" valor={valor}
                       autoFocus onValor={setValor} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`fia-desc-mot-${fiado.id}`}>Motivo</label>
          <input id={`fia-desc-mot-${fiado.id}`} className="campo py-2 text-sm" value={motivo}
                 placeholder="Opcional: acordo com a oficina…"
                 onChange={(e) => setMotivo(e.target.value)} />
        </div>
      </div>
      {demais && (
        <p className="text-xs text-erro">
          O cliente deve {brl(saldo)} — o desconto não pode ser maior.
        </p>
      )}
      {erro ? <Aviso tipo="erro">{erro}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvando || !valor || valor <= 0 || demais}
                onClick={() => aoConfirmar(valor ?? 0, motivo)}>
          {salvando ? 'Lançando…' : `Descontar ${brl(valor ?? 0)}`}
        </button>
      </div>
    </div>
  );
}

/** O acerto: pode ser parcial ("levo hoje e acerto metade sexta") e, desde a
 *  065, PEÇA A PEÇA — marcando quais linhas da venda o dinheiro cobre. */
function PainelAcerto({ fiado, aoFechar, aoReceber }: {
  fiado: FiadoRow; aoFechar: () => void; aoReceber: () => void;
}) {
  const [valor, setValor] = useState<number | null>(Number(fiado.saldo));
  const [data, setData] = useState(hojeISO());
  const [forma, setForma] = useState<PaymentMethod>('dinheiro');
  const [marcados, setMarcados] = useState<string[]>([]);

  // As três consultas usam as MESMAS chaves da aba Vendas — abrir a venda lá e
  // acertar aqui aproveitam o mesmo cache.
  //
  // E as peças vêm da função de GERÊNCIA (28/09/2026): o acerto PEÇA A PEÇA
  // (065) é marcar quais linhas da venda o dinheiro cobre, e a venda é de quem
  // vendeu. Lendo `sale_items` direto, o gerente veria a lista de peças vazia
  // e só sobraria o valor livre — perderia a ferramenta sem nenhum aviso.
  const itens = useQuery({
    queryKey: ['itens-venda', fiado.sale_id],
    queryFn: () => itensDaVendaParaGerencia(fiado.sale_id),
  });
  const quitacao = useQuery({
    queryKey: ['quitacao', fiado.sale_id],
    queryFn: () => quitacaoDaVenda(fiado.sale_id),
  });
  const devolucoes = useQuery({
    queryKey: ['devolucoes-venda', fiado.sale_id],
    queryFn: () => devolucoesDaVenda(fiado.sale_id),
  });

  const pagos = new Set(quitacao.data?.itens_pagos ?? []);
  // Quanto de cada linha já voltou. Devolver a peça abate a dívida na hora
  // (065), então a linha inteira devolvida já saiu da conta — não é algo a
  // receber. A parcialmente devolvida vira caso de valor livre: o subtotal
  // congelado não diz mais o que se deve dela.
  const devolvido: Record<string, number> = {};
  for (const d of devolucoes.data ?? []) {
    for (const i of d.itens) {
      devolvido[i.sale_item_id] = (devolvido[i.sale_item_id] ?? 0) + Number(i.quantidade);
    }
  }
  const lista = itens.data ?? [];
  const saldo = Number(fiado.saldo);

  // O valor de um acerto por peça é a soma dos subtotais, LIMITADA ao saldo —
  // a mesma conta que o banco refaz (a venda pode ter sido paga em parte com
  // vale, ou já ter levado desconto). Mostrar aqui um número que o servidor
  // vai recusar seria pior do que não mostrar nada.
  const somaMarcados = lista
    .filter((i) => marcados.includes(i.id))
    .reduce((s, i) => s + Number(i.subtotal), 0);
  const porPeca = marcados.length > 0;
  const valorFinal = porPeca ? Math.min(somaMarcados, saldo) : valor ?? 0;

  const alternar = (id: string) => setMarcados((m) =>
    m.includes(id) ? m.filter((x) => x !== id) : [...m, id]);

  const receber = useMutation({
    mutationFn: () => receberFiado({
      contaId: fiado.id, valor: valorFinal, data, forma,
      itens: porPeca ? marcados : undefined,
    }),
    onSuccess: aoReceber,
  });

  return (
    <div className="mt-2 space-y-3 border border-signal/40 bg-signal/5 p-3">
      <p className="text-xs text-mute">
        {fiado.cliente} deve <strong>{brl(saldo)}</strong>. Pode receber
        em partes — o saldo continua aqui até zerar.
      </p>

      {/* AS PEÇAS DA VENDA. O dono pediu isto em 27/08/2026: "pagou X peça e
          deixou Y e Z sem pagar". Marcar linha por linha; quem já foi paga
          fica marcada e travada. */}
      {lista.length > 0 && (
        <div className="space-y-1">
          <p className="rotulo">Peças desta venda</p>
          <ul className="space-y-1">
            {lista.map((i) => {
              const jaPago = pagos.has(i.id);
              const voltou = devolvido[i.id] ?? 0;
              const toda = voltou >= Number(i.quantidade);
              const travado = jaPago || voltou > 0;
              return (
                <li key={i.id}>
                  <label className={`flex items-center gap-2 text-xs ${travado ? 'text-dim' : ''}`}>
                    <input type="checkbox" disabled={travado}
                           checked={jaPago || marcados.includes(i.id)}
                           onChange={() => alternar(i.id)} />
                    <span className="min-w-0 flex-1 truncate">
                      {Number(i.quantidade)}× {nomeDoItemVendido(i)}
                      {jaPago && <span className="ml-1 text-ok">(paga)</span>}
                      {!jaPago && toda && (
                        <span className="ml-1 text-ok">(devolvida — já saiu da dívida)</span>
                      )}
                      {!jaPago && voltou > 0 && !toda && (
                        <span className="ml-1 text-alerta">
                          (devolução parcial — receba pelo valor)
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums">{brl(Number(i.subtotal))}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-3">
        <div>
          <label className="rotulo" htmlFor={`fia-valor-${fiado.id}`}>Recebendo (R$)</label>
          {/* Com peças marcadas o campo SAI da tela em vez de ficar desabilitado:
              o CampoNumero guarda o texto digitado até o blur, e um input travado
              continuaria exibindo o que a pessoa tinha digitado antes de marcar —
              um número que não é o que vai ser cobrado. */}
          {porPeca ? (
            <>
              <p className="py-2 text-lg font-black tabular-nums">{brl(valorFinal)}</p>
              <p className="text-xs text-dim">
                {somaMarcados > saldo
                  ? 'as peças somam mais que a dívida — recebe o saldo'
                  : 'soma das peças marcadas'}
                {' · '}desmarque para digitar outro valor
              </p>
            </>
          ) : (
            <CampoNumero id={`fia-valor-${fiado.id}`} className="campo py-2" valor={valor}
                         onValor={setValor} />
          )}
        </div>
        <div>
          <label className="rotulo" htmlFor={`fia-data-${fiado.id}`}>Data</label>
          {/* 059: esta data manda no CAIXA também. O acerto que aconteceu
              ontem entra no caixa de ontem — senão a gaveta de ontem fecha
              faltando dinheiro e a de hoje, sobrando. Futuro é recusado pelo
              banco; o `max` evita a viagem até lá. */}
          <input id={`fia-data-${fiado.id}`} type="date" className="campo py-2" value={data}
                 max={hojeISO()} onChange={(e) => setData(e.target.value)} />
          <p className="mt-1 text-xs text-dim">
            {data === hojeISO()
              ? 'Entra no caixa agora.'
              : 'Entra no caixa NESTE dia — o dia em que o cliente pagou.'}
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor={`fia-forma-${fiado.id}`}>Como pagou</label>
          <select id={`fia-forma-${fiado.id}`} className="campo py-2" value={forma}
                  onChange={(e) => setForma(e.target.value as PaymentMethod)}>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="debito">Débito</option>
            <option value="credito">Crédito</option>
            <option value="transferencia">Transferência</option>
          </select>
        </div>
      </div>
      {receber.error ? <Aviso tipo="erro">{mensagemDeErro(receber.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={receber.isPending || valorFinal <= 0}
                onClick={() => receber.mutate()}>
          {receber.isPending ? 'Lançando…' : `Receber ${brl(valorFinal)}`}
        </button>
      </div>
    </div>
  );
}
